import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Form, Formik, FormikProps } from "formik"
import {
  CreateSubscriptionPayload,
  GooglePlace,
  CreateSubscriptionResponse,
  SubscriptionDetails
} from "../../../../types"
import SidePanel from "../../side-panel"
import { engagementFormSchema } from "./engagement-form-schema"
import ManageEngagementBasic from "./sections/basic"
import ManageEngagementMisc from "./sections/misc"
import ManageEngagementSelectors from "./sections/selectors"
import {
  useAppSelector,
  useToast,
  useValidateEmailAndNMLS
} from "../../../hooks"
import { selectUserDetails } from "../../../../redux/slices/auth"
import {
  convertGooglePlaceToAddressObj,
  convertSubscriptionDetailsToPayload,
  validateZipcodeForPayload
} from "../../../utils"
import {
  useCreateSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useFetchLeadDetailsQuery
} from "../../../../redux/queries"

interface Props {
  brandId?: string
  data?: SubscriptionDetails
  open: boolean
  leadId?: string
  onClose: () => void
  submitCallback?: (response: CreateSubscriptionResponse) => void
}

const ManageEngagementPanel: FC<Props> = ({
  brandId,
  data,
  open,
  leadId,
  onClose,
  submitCallback
}) => {
  const userDetails = useAppSelector(selectUserDetails)
  const { pushErrorToast, pushSuccessToast } = useToast()

  const { validateSubscriptionEmail } = useValidateEmailAndNMLS()

  const selectedbrandId = useMemo(() => {
    return (
      userDetails?.org?.brands?.find(
        brand => brand.default_loid === userDetails?.id
      )?.id ??
      brandId ??
      userDetails?.brand_id ??
      ""
    )
  }, [
    brandId,
    userDetails?.brand_id,
    userDetails?.id,
    userDetails?.org?.brands
  ])

  const { data: leadDetails } = useFetchLeadDetailsQuery(leadId ?? "", {
    skip: !leadId
  })

  // Google Place object, when submitting parse this object to get state, city, address, zipcode
  const [googlePlace, setGooglePlace] = useState<GooglePlace>()

  const validate = useCallback(
    async (payload: CreateSubscriptionPayload | SubscriptionDetails) => {
      const errors: any = {}

      // Email validation (existing)
      const emailErrors = await validateSubscriptionEmail(
        payload,
        selectedbrandId
      )
      if (emailErrors) {
        Object.assign(errors, emailErrors)
      }

      // Zipcode validation - only required when location details are shown
      const zipcodeErrors = validateZipcodeForPayload(
        payload as CreateSubscriptionPayload,
        googlePlace
      )
      Object.assign(errors, zipcodeErrors)

      return errors
    },
    [validateSubscriptionEmail, selectedbrandId, googlePlace]
  )

  const [
    createSubscription,
    {
      data: createSubscriptionData,
      isSuccess: isSuccessCreate,
      isError: isErrorCreate,
      isLoading: isLoadingCreate,
      error: createSubscriptionError,
      reset: resetCreateSubscription
    }
  ] = useCreateSubscriptionMutation()
  const [
    updateSubscription,
    {
      data: updateSubscriptionData,
      isSuccess: isSuccessUpdate,
      isError: isErrorUpdate,
      isLoading: isLoadingUpdate,
      reset: resetUpdateSubscription
    }
  ] = useUpdateSubscriptionMutation()

  const formRef = useRef<FormikProps<CreateSubscriptionPayload>>(null)

  const isCreateMode = !data?.id
  const isLoading = isLoadingCreate || isLoadingUpdate

  const setFormField = (name: string, val: string | number | boolean) => {
    formRef.current?.setFieldValue(name, val)
  }

  const resetForm = useCallback(() => {
    resetCreateSubscription()
    resetUpdateSubscription()
    formRef.current?.resetForm()
  }, [resetCreateSubscription, resetUpdateSubscription])

  const onSubmit = async () => {
    if (formRef.current?.values && formRef.current.isValid) {
      if (isCreateMode) {
        createSubscription(formRef.current.values)
      } else {
        updateSubscription({ id: data.id, ...formRef.current.values })
      }
    }
  }
  // Merge lead email into details since LeadDetails doesn't have email field
  const leadDetailsWithEmail = leadDetails?.details
    ? {
        ...leadDetails.details,
        first_name: leadDetails.first_name,
        last_name: leadDetails.last_name
      }
    : undefined

  const baseValues = convertSubscriptionDetailsToPayload(
    data
      ? data
      : leadDetails
      ? ({ email: leadDetails.email } as any)
      : undefined,
    userDetails?.id,
    leadDetailsWithEmail,
    isCreateMode
  )

  const INITIAL_VALUES: CreateSubscriptionPayload = isCreateMode
    ? {
        ...baseValues,
        loan_type: baseValues.loan_type || "conventional",
        loan_term: baseValues.loan_term || "30",
        property_type: baseValues.property_type || "single_family_home",
        residency_type: baseValues.residency_type || "primary_home"
      }
    : baseValues

  useEffect(() => {
    if (googlePlace) {
      const address = convertGooglePlaceToAddressObj(googlePlace)
      setFormField("city", address.city ?? "")
      setFormField("county", address.county ?? "")
      setFormField("state", address.state ?? "")
      setFormField("street_address", address.street_address ?? "")
      setFormField("zipcode", address.zipcode ?? "")
    }
  }, [googlePlace])

  const currentData = useMemo(
    () => (isCreateMode ? createSubscriptionData : updateSubscriptionData),
    [isCreateMode, createSubscriptionData, updateSubscriptionData]
  )

  const currentStatus = useMemo(
    () => currentData?.status,
    [currentData?.status]
  )

  const currentIsSuccess = useMemo(
    () => (isCreateMode ? isSuccessCreate : isSuccessUpdate),
    [isCreateMode, isSuccessCreate, isSuccessUpdate]
  )

  const currentIsError = useMemo(
    () => (isCreateMode ? isErrorCreate : isErrorUpdate),
    [isCreateMode, isErrorCreate, isErrorUpdate]
  )

  const getErrorMessage = useCallback(() => {
    if (!isCreateMode || !createSubscriptionError) return undefined
    if ("message" in createSubscriptionError)
      return createSubscriptionError.message
    if ("data" in createSubscriptionError)
      return String(createSubscriptionError.data)
    return undefined
  }, [isCreateMode, createSubscriptionError])

  useEffect(() => {
    if (currentIsSuccess && currentStatus === "success") {
      onClose()
      pushSuccessToast(
        `Successfully ${isCreateMode ? "created" : "updated"} subscription`
      )
      resetForm()
      submitCallback?.(currentData!)
    } else if (currentIsError || currentStatus === "error") {
      const errorMessage = getErrorMessage()
      pushErrorToast(
        <>
          <div>Failed to {isCreateMode ? "create" : "update"} subscription</div>
          {errorMessage && (
            <div className="text-stone-400 text-sm">{errorMessage}</div>
          )}
        </>
      )
      resetCreateSubscription()
      resetUpdateSubscription()
    }
  }, [
    currentIsSuccess,
    currentStatus,
    currentIsError,
    currentData,
    onClose,
    pushSuccessToast,
    pushErrorToast,
    isCreateMode,
    resetForm,
    submitCallback,
    getErrorMessage,
    resetCreateSubscription,
    resetUpdateSubscription
  ])

  return (
    <Formik
      initialValues={INITIAL_VALUES}
      validationSchema={engagementFormSchema}
      onSubmit={onSubmit}
      innerRef={formRef}
      validateOnBlur
      validateOnChange
      validate={validate}
      enableReinitialize
    >
      {({ values, errors, isValid, dirty }) => {
        console.log("errors", errors)

        return (
          <SidePanel
            title={`${isCreateMode ? "Add new" : "Edit"} subscription`}
            open={open}
            onClose={onClose}
            actions={[
              {
                label: `${isCreateMode ? "Add" : "Update"} subscription`,
                type: "primary",
                onClick: onSubmit,
                disabled: isLoading || !isValid || !dirty
              }
            ]}
          >
            <Form>
              <ManageEngagementBasic
                values={values}
                loSearchParams={{ brand_id: selectedbrandId }}
                onlyAdmins={false}
                setFormField={setFormField}
                setGooglePlace={setGooglePlace}
                googlePlace={googlePlace}
              />
              <ManageEngagementSelectors
                values={values}
                setFormField={setFormField}
              />
              <ManageEngagementMisc
                values={values}
                setFormField={setFormField}
                hasSendRateUpdateEmails
              />
            </Form>
          </SidePanel>
        )
      }}
    </Formik>
  )
}

export default ManageEngagementPanel
